import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Network as NetworkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const T = {
  de: { title: 'Vermittler-Login', subtitle: 'Löwenherz Invest AG · Vermittler-Portal',
        email: 'E-Mail', password: 'Passwort', submit: 'Anmelden', loading: 'Wird angemeldet...',
        noAccount: 'Noch kein Vermittler-Konto?', register: 'Jetzt Vermittler werden' },
  en: { title: 'Mediator Login', subtitle: 'Löwenherz Invest AG · Mediator Portal',
        email: 'Email', password: 'Password', submit: 'Sign in', loading: 'Signing in...',
        noAccount: 'No mediator account yet?', register: 'Become a mediator' },
  ru: { title: 'Вход для посредников', subtitle: 'Löwenherz Invest AG · Портал посредников',
        email: 'Email', password: 'Пароль', submit: 'Войти', loading: 'Вход...',
        noAccount: 'Нет аккаунта посредника?', register: 'Стать посредником' },
  vi: { title: 'Đăng nhập người môi giới', subtitle: 'Löwenherz Invest AG · Cổng người môi giới',
        email: 'Email', password: 'Mật khẩu', submit: 'Đăng nhập', loading: 'Đang đăng nhập...',
        noAccount: 'Chưa có tài khoản môi giới?', register: 'Trở thành người môi giới' },
};

export const MediatorLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = T[language] || T.de;

  React.useEffect(() => {
    if (user?.role === 'mediator') {
      navigate('/mediator/dashboard');
    } else if (user && ['admin', 'editor'].includes(user.role)) {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login({ email, password });
      if (result.success) {
        const role = result.user?.role;
        if (role === 'mediator') {
          toast.success('Login erfolgreich!');
          navigate('/mediator/dashboard');
        } else if (role === 'admin' || role === 'editor') {
          toast.success('Login als Admin/Editor – Weiterleitung...');
          navigate('/admin/dashboard');
        } else {
          toast.error('Keine Vermittler-Berechtigung');
        }
      } else {
        toast.error(result.error || 'Login fehlgeschlagen');
      }
    } catch {
      toast.error('Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#1a1a1a] border-[#C4A55A]/30">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C4A55A]/20 flex items-center justify-center">
            <NetworkIcon className="h-8 w-8 text-[#C4A55A]" />
          </div>
          <CardTitle className="text-3xl text-white">{t.title}</CardTitle>
          <CardDescription className="text-gray-400">
            {t.subtitle}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">{t.email}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                required
                data-testid="mediator-login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">{t.password}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                required
                data-testid="mediator-login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="mediator-login-submit"
              className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold transition-all duration-300 disabled:opacity-50"
            >
              {loading ? t.loading : t.submit}
            </Button>
          </form>
          <div className="text-center mt-5 pt-4 border-t border-white/10">
            <p className="text-sm text-gray-400">
              {t.noAccount}{' '}
              <a
                href="/vermittler/register"
                data-testid="mediator-login-register-link"
                className="text-[#C4A55A] hover:text-[#D4B56A] font-medium underline"
              >
                {t.register}
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
